// AiasPay Client SDK
// KG이니시스 V2(inicis_v2) 일반결제 기반으로 전환
// Docs: https://developers.portone.io/opi/ko/integration/pg/v2/inicis-v2?v=v2
// - requestPayment 사용 (requestIssueBillingKey는 BILLING_ENABLED=true 시에만)
// - subscribe()는 BILLING_ENABLED 플래그로 차단됨 (코드 삭제 X, 플래그로 비활성)
// - purchase({appId, planId, redirectUrl}) 추가 — 단건 1개월 이용권 결제
import { requestIssueBillingKey, requestPayment } from "@portone/browser-sdk/v2";

export interface AiasPayConfig {
  baseUrl?: string;
}

export interface PurchaseParams {
  appId: string;
  planId: string;
  addonCafes?: number;
  addonKeywords?: number;
  redirectUrl?: string;
  baseUrl?: string;
}

export interface SubscribeParams {
  appId: string;
  planId: string;
  redirectUrl?: string;
  baseUrl?: string;
}

export interface ChangeCardParams {
  appId: string;
  redirectUrl?: string;
  baseUrl?: string;
}

export interface HandleRedirectParams {
  baseUrl?: string;
}

let globalConfig: AiasPayConfig = {};

export function configure(config: AiasPayConfig) {
  globalConfig = { ...globalConfig, ...config };
}

function getBaseUrl(overrideUrl?: string): string {
  if (overrideUrl) return overrideUrl.replace(/\/+$/, "");
  if (globalConfig.baseUrl) return globalConfig.baseUrl.replace(/\/+$/, "");

  if (typeof document !== "undefined") {
    const currentScript = document.currentScript as HTMLScriptElement | null;
    if (currentScript && currentScript.src) {
      try {
        const scriptUrl = new URL(currentScript.src);
        return scriptUrl.origin;
      } catch {}
    }
  }

  if (typeof window !== "undefined" && window.location) {
    return window.location.origin;
  }

  return "https://pay.aiasaas.com";
}

async function fetchApi<T = any>(
  endpoint: string,
  options: { method?: string; body?: any; baseUrl?: string } = {}
): Promise<T> {
  const base = getBaseUrl(options.baseUrl);
  const url = `${base}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;

  const res = await fetch(url, {
    method: options.method || "GET",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const data = await res.json().catch(() => ({
    ok: false,
    code: "PARSE_ERROR",
    message: "Failed to parse JSON response from pay server",
  }));

  return data as T;
}

export interface PurchaseWithSessionParams {
  sessionToken: string;
  redirectUrl?: string;
  baseUrl?: string;
}

// ─── 프로필 입력 모달 ──────────────────────────────────────────────────────────
// KG이니시스 V2 customer.fullName 필수, PC결제 phone+email 필수 요건 충족
// 최상위 z-index, 모바일 대응 독립 스타일
function showProfileModal(baseUrl?: string): Promise<{ ok: boolean; name?: string; phone?: string; error?: string }> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve({ ok: false, error: "NO_DOM" });
      return;
    }

    const OVERLAY_ID = "aias-pay-profile-overlay";
    if (document.getElementById(OVERLAY_ID)) {
      document.getElementById(OVERLAY_ID)!.remove();
    }

    const css = `
      #aias-pay-profile-overlay {
        position: fixed; inset: 0; background: rgba(0,0,0,.55);
        z-index: 2147483647; display: flex; align-items: center; justify-content: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        box-sizing: border-box; padding: 16px;
      }
      #aias-pay-profile-box {
        background: #fff; border-radius: 12px; padding: 28px 24px;
        width: 100%; max-width: 380px; box-sizing: border-box;
        box-shadow: 0 8px 32px rgba(0,0,0,.18);
      }
      #aias-pay-profile-box h2 {
        margin: 0 0 6px; font-size: 18px; font-weight: 700; color: #111;
      }
      #aias-pay-profile-box p {
        margin: 0 0 18px; font-size: 13px; color: #666;
      }
      .aias-field { margin-bottom: 14px; }
      .aias-field label { display: block; font-size: 13px; font-weight: 600; color: #333; margin-bottom: 5px; }
      .aias-field input[type=text], .aias-field input[type=tel] {
        width: 100%; box-sizing: border-box; padding: 10px 12px;
        border: 1px solid #d0d5dd; border-radius: 8px; font-size: 15px; outline: none;
        transition: border .15s; color: #111 !important; background-color: #fff !important;
      }
      .aias-field input:focus { border-color: #2563eb; }
      .aias-consent { display: flex; align-items: flex-start; gap: 8px; margin: 10px 0 18px; }
      .aias-consent input[type=checkbox] { margin-top: 3px; flex-shrink: 0; }
      .aias-consent span { font-size: 12px; color: #555; line-height: 1.5; }
      .aias-btn-row { display: flex; gap: 10px; }
      .aias-btn { flex: 1; padding: 11px; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer; border: none; }
      .aias-btn-primary { background: #2563eb; color: #fff; }
      .aias-btn-primary:hover { background: #1d4ed8; }
      .aias-btn-cancel { background: #f1f5f9; color: #334155; }
      .aias-btn-cancel:hover { background: #e2e8f0; }
      #aias-profile-error { color: #dc2626; font-size: 13px; margin-top: 8px; min-height: 18px; }
    `;

    const styleEl = document.createElement("style");
    styleEl.id = "aias-pay-profile-style";
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    const overlay = document.createElement("div");
    overlay.id = OVERLAY_ID;
    overlay.innerHTML = `
      <form id="aias-pay-profile-box" novalidate autocomplete="on">
        <h2>결제 정보 입력</h2>
        <p>KG이니시스 결제창 실행을 위해 아래 정보가 필요합니다.</p>
        <div class="aias-field">
          <label for="aias-name">이름 <span style="color:#dc2626">*</span></label>
          <input id="aias-name" type="text" placeholder="홍길동" autocomplete="name" />
        </div>
        <div class="aias-field">
          <label for="aias-phone">휴대폰 번호 <span style="color:#dc2626">*</span></label>
          <input id="aias-phone" type="tel" placeholder="01012345678" autocomplete="tel" inputmode="numeric" />
        </div>
        <div class="aias-consent">
          <input type="checkbox" id="aias-consent" />
          <span>개인정보 수집·이용에 동의합니다. (결제 처리 목적, 5년 보관 후 파기)</span>
        </div>
        <div class="aias-btn-row">
          <button type="button" class="aias-btn aias-btn-cancel" id="aias-profile-cancel">취소</button>
          <button type="submit" class="aias-btn aias-btn-primary" id="aias-profile-submit">확인</button>
        </div>
        <div id="aias-profile-error"></div>
      </form>
    `;
    document.body.appendChild(overlay);

    const formEl    = document.getElementById("aias-pay-profile-box") as HTMLFormElement;
    const nameEl    = document.getElementById("aias-name") as HTMLInputElement;
    const phoneEl   = document.getElementById("aias-phone") as HTMLInputElement;
    const consentEl = document.getElementById("aias-consent") as HTMLInputElement;
    const errorEl   = document.getElementById("aias-profile-error")!;
    const submitBtn = document.getElementById("aias-profile-submit") as HTMLButtonElement;
    const cancelBtn = document.getElementById("aias-profile-cancel")!;

    // 이름 필드 자동 포커스
    setTimeout(() => nameEl?.focus(), 50);

    function cleanup() {
      overlay.remove();
      styleEl.remove();
    }

    cancelBtn.addEventListener("click", () => {
      cleanup();
      resolve({ ok: false, error: "CANCELLED" });
    });

    // 엔터(form submit)와 버튼 클릭 모두 동일 핸들러
    formEl.addEventListener("submit", async (e) => {
      e.preventDefault();

      const name  = nameEl.value.trim();
      const phone = phoneEl.value.replace(/\D/g, "");
      errorEl.textContent = "";

      if (!name) { errorEl.textContent = "이름을 입력해주세요."; return; }
      if (phone.length < 10 || phone.length > 11) { errorEl.textContent = "올바른 휴대폰 번호를 입력해주세요."; return; }
      if (!consentEl.checked) { errorEl.textContent = "개인정보 수집·이용에 동의해주세요."; return; }

      submitBtn.textContent = "저장 중...";
      submitBtn.disabled = true;

      try {
        const res = await fetchApi("/api/v1/profile", {
          method: "POST",
          body: { name, phone },
          baseUrl,
        });
        if (!res.ok) {
          errorEl.textContent = res.message || "저장에 실패했습니다.";
          submitBtn.textContent = "확인";
          submitBtn.disabled = false;
          return;
        }
        cleanup();
        resolve({ ok: true, name, phone });
      } catch (e: any) {
        errorEl.textContent = e.message || "오류가 발생했습니다.";
        submitBtn.textContent = "확인";
        submitBtn.disabled = false;
      }
    });
  });
}

// ─── PAYMENT_NOT_READY 알림 ────────────────────────────────────────────────────
function showPaymentNotReady(message: string): void {
  if (typeof document === "undefined") return;
  const TOAST_ID = "aias-pay-not-ready-toast";
  const existing = document.getElementById(TOAST_ID);
  if (existing) existing.remove();

  const el = document.createElement("div");
  el.id = TOAST_ID;
  el.style.cssText = [
    "position:fixed", "top:24px", "left:50%", "transform:translateX(-50%)",
    "z-index:2147483647", "background:#1e293b", "color:#f8fafc",
    "padding:12px 20px", "border-radius:8px", "font-size:14px",
    "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    "box-shadow:0 4px 16px rgba(0,0,0,.35)", "max-width:90vw", "text-align:center",
    "pointer-events:none",
  ].join(";");
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 4000);
}

// ─── purchase — 단건 1개월 이용권 결제 (KG이니시스 V2 requestPayment) ────────
/**
 * AiasPay.purchase({ appId, planId, redirectUrl })
 * 1. POST /api/v1/checkout → 세션·paymentId 생성
 * 2. 프로필 미완성이면 모달 → POST /api/v1/profile 저장
 * 3. requestPayment (KG이니시스 V2 일반결제창)
 * 4. POST /api/v1/confirm → 1개월 이용기간 부여
 */
export async function purchase(params: PurchaseParams) {
  try {
    const { appId, planId, addonCafes, addonKeywords, redirectUrl, baseUrl } = params;

    // Step 1: Checkout 세션 생성
    let checkoutRes: any = await fetchApi("/api/v1/checkout", {
      method: "POST",
      body: { appId, planId, addonCafes: addonCafes ?? 0, addonKeywords: addonKeywords ?? 0 },
      baseUrl,
    });

    if (!checkoutRes.ok) {
      if (checkoutRes.code === "PAYMENT_NOT_READY") {
        showPaymentNotReady(checkoutRes.message || "결제 준비 중입니다. 잠시 후 다시 이용해 주세요.");
      }
      return checkoutRes;
    }

    // Step 2: 프로필 미완성이면 모달 표시 후 재시도
    if (checkoutRes.needsProfile) {
      const modalResult = await showProfileModal(baseUrl);
      if (!modalResult.ok) {
        return { ok: false, code: "PROFILE_CANCELLED", message: "결제 정보 입력이 취소되었습니다." };
      }

      checkoutRes = await fetchApi("/api/v1/checkout", {
        method: "POST",
        body: { appId, planId, addonCafes: addonCafes ?? 0, addonKeywords: addonKeywords ?? 0 },
        baseUrl,
      });

      if (!checkoutRes.ok) return checkoutRes;
      if (checkoutRes.needsProfile) {
        return { ok: false, code: "PROFILE_REQUIRED", message: "결제 정보 저장에 실패했습니다. 다시 시도해주세요." };
      }
    }

    const { sessionId, paymentId, storeId, channelKey, orderName, amount, customer } = checkoutRes;

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("aias_pay_session_id", sessionId);
      sessionStorage.setItem("aias_pay_payment_id", paymentId);
      sessionStorage.setItem("aias_pay_app_id", appId);
      sessionStorage.setItem("aias_pay_action", "purchase");
    }

    // Step 3: requestPayment — KG이니시스 V2 일반결제창
    // windowType은 반드시 { pc, mobile } 객체 형식이어야 함 (문자열 불가)
    // Ref: https://developers.portone.io/opi/ko/integration/pg/v2/inicis-v2?v=v2
    const payResult = await requestPayment({
      storeId,
      channelKey,
      paymentId,
      orderName,
      totalAmount: amount,
      currency: "KRW",
      payMethod: "CARD",
      customer,
      windowType: { pc: "IFRAME", mobile: "REDIRECTION" },
      redirectUrl: redirectUrl || (typeof window !== "undefined" ? window.location.href : undefined),
    } as any);

    // 사용자 취소 또는 오류
    if (payResult && (payResult as any).code != null) {
      const errCode = (payResult as any).code;
      const errMsg  = (payResult as any).message || "결제가 취소되었거나 실패했습니다.";
      console.error("[AiasPay] requestPayment 실패:", { code: errCode, message: errMsg, raw: payResult });
      return { ok: false, code: errCode, message: errMsg };
    }

    // Step 4: Confirm — PortOne 직접 조회로 금액 재확인 후 이용기간 부여
    const confirmRes = await fetchApi("/api/v1/confirm", {
      method: "POST",
      body: { sessionId, paymentId },
      baseUrl,
    });

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("aias_pay_session_id");
      sessionStorage.removeItem("aias_pay_payment_id");
      sessionStorage.removeItem("aias_pay_app_id");
      sessionStorage.removeItem("aias_pay_action");
    }

    return confirmRes;
  } catch (err: any) {
    return { ok: false, code: "CLIENT_ERROR", message: err.message || "결제 처리 중 오류가 발생했습니다." };
  }
}

// ─── purchaseWithSession — server-initiated checkout 흐름 ────────────────────
/**
 * AiasPay.purchaseWithSession({ sessionToken, redirectUrl })
 * 1) GET /api/v1/checkout/session → 세션 정보 조회
 * 2) needsProfile이면 프로필 모달 → POST /api/v1/checkout/profile
 * 3) POST /api/v1/checkout/start → requestPayment
 * 4) POST /api/v1/checkout/complete
 */
export async function purchaseWithSession(params: PurchaseWithSessionParams) {
  const { sessionToken, redirectUrl, baseUrl } = params;

  // 1. 세션 조회
  const sessionRes: any = await fetchApi(`/api/v1/checkout/session?s=${encodeURIComponent(sessionToken)}`, { baseUrl });
  if (!sessionRes.ok) {
    return { ok: false, code: sessionRes.code || "SESSION_ERROR", message: sessionRes.message || "세션 조회에 실패했습니다." };
  }

  // 2. 프로필 필요 시 모달
  if (sessionRes.needsProfile) {
    const modalResult = await showProfileModal(baseUrl);
    if (!modalResult.ok) {
      return { ok: false, code: "PROFILE_CANCELLED", message: "결제 정보 입력이 취소되었습니다." };
    }
    // 프로필 저장 — /api/v1/checkout/profile 엔드포인트로 직접 저장
    const saveRes: any = await fetchApi("/api/v1/checkout/profile", {
      method: "POST",
      body: { s: sessionToken, name: modalResult.name, phone: modalResult.phone },
      baseUrl,
    });
    if (!saveRes.ok) {
      return { ok: false, code: "PROFILE_SAVE_FAILED", message: saveRes.message || "프로필 저장에 실패했습니다." };
    }
  }

  // 3. start
  const startRes: any = await fetchApi("/api/v1/checkout/start", { method: "POST", body: { s: sessionToken }, baseUrl });
  if (!startRes.ok) {
    if (startRes.code === "PAYMENT_NOT_READY") {
      showPaymentNotReady(startRes.message || "결제 준비 중입니다. 잠시 후 다시 이용해 주세요.");
    }
    return { ok: false, code: startRes.code || "START_FAILED", message: startRes.message || "결제 시작에 실패했습니다." };
  }

  const { storeId, channelKey, paymentId, orderName, totalAmount, currency, customer: custInfo, payMethod } = startRes;

  // sessionStorage에 저장 (모바일 리다이렉트 복귀용)
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem("aias_pay_session_token", sessionToken);
    sessionStorage.setItem("aias_pay_payment_id", paymentId);
    sessionStorage.setItem("aias_pay_action", "purchaseWithSession");
  }

  // 4. requestPayment
  let paymentResult: any;
  try {
    paymentResult = await requestPayment({
      storeId,
      channelKey,
      paymentId,
      orderName,
      totalAmount,
      currency: currency || "KRW",
      payMethod: payMethod || "CARD",
      customer: custInfo,
      windowType: { pc: "IFRAME", mobile: "REDIRECTION" },
      redirectUrl: redirectUrl || (typeof window !== "undefined" ? window.location.href : undefined),
    } as any);
  } catch (e: any) {
    console.error("[AiasPay] requestPayment exception:", e);
    return { ok: false, code: "REQUEST_PAYMENT_EXCEPTION", message: e.message || "결제창 호출 중 오류가 발생했습니다." };
  }

  if (paymentResult?.code) {
    console.error("[AiasPay] requestPayment failed:", paymentResult.code, paymentResult.message);
    return { ok: false, code: paymentResult.code, message: paymentResult.message || "결제에 실패했습니다." };
  }

  // 5. complete
  const completeRes: any = await fetchApi("/api/v1/checkout/complete", {
    method: "POST",
    body: { s: sessionToken, paymentId },
    baseUrl,
  });

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem("aias_pay_session_token");
    sessionStorage.removeItem("aias_pay_payment_id");
    sessionStorage.removeItem("aias_pay_action");
  }

  if (!completeRes.ok) {
    return { ok: false, code: completeRes.code || "COMPLETE_FAILED", message: completeRes.message || "결제 완료 처리에 실패했습니다." };
  }

  return { ok: true, planId: completeRes.planId, periodEnd: completeRes.periodEnd, returnUrl: completeRes.returnUrl };
}

// ─── handleRedirect — 모바일 리다이렉트 복귀 처리 ────────────────────────────
export async function handleRedirect(options: HandleRedirectParams = {}) {
  try {
    if (typeof window === "undefined") {
      return { ok: false, code: "NOT_IN_BROWSER", message: "Only available in browser" };
    }

    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");
    const message = searchParams.get("message");

    if (code) {
      return { ok: false, code, message: message || "결제 창에서 오류가 발생했습니다." };
    }

    const action = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("aias_pay_action") : "purchase";

    if (action === "purchaseWithSession") {
      // server-initiated checkout 모바일 리다이렉트 복귀
      const sessionToken =
        typeof sessionStorage !== "undefined" ? sessionStorage.getItem("aias_pay_session_token") : null;
      const paymentId =
        searchParams.get("paymentId") ||
        (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("aias_pay_payment_id") : null);

      if (!sessionToken || !paymentId) {
        return { ok: false, code: "MISSING_REDIRECT_PARAMS", message: "리다이렉트 응답 파라미터가 누락되었습니다." };
      }

      const completeRes = await fetchApi("/api/v1/checkout/complete", {
        method: "POST",
        body: { s: sessionToken, paymentId },
        baseUrl: options.baseUrl,
      });

      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("aias_pay_session_token");
        sessionStorage.removeItem("aias_pay_payment_id");
        sessionStorage.removeItem("aias_pay_action");
      }

      return completeRes;
    }

    if (action === "purchase") {
      // requestPayment 모바일 redirect: paymentId가 URL에 포함되거나 sessionStorage에 있음
      const paymentId =
        searchParams.get("paymentId") ||
        (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("aias_pay_payment_id") : null);
      const sessionId =
        typeof sessionStorage !== "undefined" ? sessionStorage.getItem("aias_pay_session_id") : null;

      if (!paymentId || !sessionId) {
        return { ok: false, code: "MISSING_REDIRECT_PARAMS", message: "리다이렉트 응답 파라미터가 누락되었습니다." };
      }

      const confirmRes = await fetchApi("/api/v1/confirm", {
        method: "POST",
        body: { sessionId, paymentId },
        baseUrl: options.baseUrl,
      });

      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("aias_pay_session_id");
        sessionStorage.removeItem("aias_pay_payment_id");
        sessionStorage.removeItem("aias_pay_app_id");
        sessionStorage.removeItem("aias_pay_action");
      }

      return confirmRes;
    }

    // 기존 billingKey 기반 리다이렉트 처리 (card_change 등 BILLING_ENABLED 시)
    const billingKey = searchParams.get("billingKey") || searchParams.get("txId");
    const sessionId =
      searchParams.get("sessionId") ||
      (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("aias_pay_session_id") : null);

    if (!billingKey || !sessionId) {
      return { ok: false, code: "MISSING_REDIRECT_PARAMS", message: "리다이렉트 응답 파라미터가 누락되었습니다." };
    }

    const confirmEndpoint = action === "card_change" ? "/api/v1/card/confirm" : "/api/v1/confirm";
    const confirmRes = await fetchApi(confirmEndpoint, {
      method: "POST",
      body: { sessionId, billingKey },
      baseUrl: options.baseUrl,
    });

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("aias_pay_session_id");
      sessionStorage.removeItem("aias_pay_app_id");
      sessionStorage.removeItem("aias_pay_action");
    }

    return confirmRes;
  } catch (err: any) {
    return { ok: false, code: "REDIRECT_HANDLER_ERROR", message: err.message || "리다이렉트 처리 중 오류가 발생했습니다." };
  }
}

// ─── subscribe — 빌링키 기반 정기결제 (BILLING_ENABLED=true 시만 동작) ──────
export async function subscribe(params: SubscribeParams) {
  // 플래그 확인은 서버에서도 하지만 클라이언트에서도 조기 차단
  // 런타임에 서버 config를 조회할 수 없으므로, checkout 응답으로 처리
  try {
    const { appId, planId, redirectUrl, baseUrl } = params;

    const checkoutRes: any = await fetchApi("/api/v1/checkout", {
      method: "POST",
      body: { appId, planId, _intent: "billing" },
      baseUrl,
    });

    if (!checkoutRes.ok) return checkoutRes;

    const { sessionId, storeId, channelKey, customerId, issueName } = checkoutRes;

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("aias_pay_session_id", sessionId);
      sessionStorage.setItem("aias_pay_app_id", appId);
      sessionStorage.setItem("aias_pay_action", "subscribe");
    }

    const issueResult = await requestIssueBillingKey({
      storeId,
      channelKey,
      billingKeyMethod: "CARD",
      issueName: issueName || `${appId} 정기결제`,
      customer: { customerId },
      redirectUrl: redirectUrl || (typeof window !== "undefined" ? window.location.href : undefined),
    });

    if (issueResult && (issueResult as any).code != null) {
      return { ok: false, code: (issueResult as any).code, message: (issueResult as any).message || "빌링키 발급이 취소되었거나 실패했습니다." };
    }

    if (!(issueResult as any)?.billingKey) {
      return { ok: false, code: "NO_BILLING_KEY", message: "빌링키가 정상적으로 발급되지 않았습니다." };
    }

    const confirmRes = await fetchApi("/api/v1/confirm", {
      method: "POST",
      body: { sessionId, billingKey: (issueResult as any).billingKey },
      baseUrl,
    });

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("aias_pay_session_id");
      sessionStorage.removeItem("aias_pay_app_id");
      sessionStorage.removeItem("aias_pay_action");
    }

    return confirmRes;
  } catch (err: any) {
    return { ok: false, code: "CLIENT_ERROR", message: err.message || "구독 처리 중 오류가 발생했습니다." };
  }
}

export async function getSubscription(appId: string, options: { baseUrl?: string } = {}) {
  return await fetchApi(`/api/v1/subscription?appId=${encodeURIComponent(appId)}`, { baseUrl: options.baseUrl });
}

export async function cancel(appId: string, options: { baseUrl?: string } = {}) {
  return await fetchApi("/api/v1/cancel", { method: "POST", body: { appId }, baseUrl: options.baseUrl });
}

export async function resume(appId: string, options: { baseUrl?: string } = {}) {
  return await fetchApi("/api/v1/resume", { method: "POST", body: { appId }, baseUrl: options.baseUrl });
}

export async function changePlan(appId: string, planId: string, options: { baseUrl?: string } = {}) {
  return await fetchApi("/api/v1/change-plan", { method: "POST", body: { appId, planId }, baseUrl: options.baseUrl });
}

export async function changeCard(params: ChangeCardParams) {
  try {
    const { appId, redirectUrl, baseUrl } = params;

    const checkoutRes: any = await fetchApi("/api/v1/card/checkout", { method: "POST", body: { appId }, baseUrl });
    if (!checkoutRes.ok) return checkoutRes;

    const { sessionId, storeId, channelKey, customerId, issueName } = checkoutRes;

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("aias_pay_session_id", sessionId);
      sessionStorage.setItem("aias_pay_app_id", appId);
      sessionStorage.setItem("aias_pay_action", "card_change");
    }

    const issueResult = await requestIssueBillingKey({
      storeId,
      channelKey,
      billingKeyMethod: "CARD",
      issueName: issueName || `${appId} 결제 카드 변경`,
      customer: { customerId },
      redirectUrl: redirectUrl || (typeof window !== "undefined" ? window.location.href : undefined),
    });

    if (issueResult && (issueResult as any).code != null) {
      return { ok: false, code: (issueResult as any).code, message: (issueResult as any).message || "카드 변경이 취소되었거나 실패했습니다." };
    }

    if (!(issueResult as any)?.billingKey) {
      return { ok: false, code: "NO_BILLING_KEY", message: "새 카드의 빌링키가 정상적으로 발급되지 않았습니다." };
    }

    const confirmRes = await fetchApi("/api/v1/card/confirm", {
      method: "POST",
      body: { sessionId, billingKey: (issueResult as any).billingKey },
      baseUrl,
    });

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("aias_pay_session_id");
      sessionStorage.removeItem("aias_pay_app_id");
      sessionStorage.removeItem("aias_pay_action");
    }

    return confirmRes;
  } catch (err: any) {
    return { ok: false, code: "CLIENT_ERROR", message: err.message || "카드 변경 처리 중 오류가 발생했습니다." };
  }
}

export function openMyPage(options: { baseUrl?: string; target?: string } = {}) {
  const base = getBaseUrl(options.baseUrl);
  const myPageUrl = `${base}/my`;
  if (typeof window !== "undefined") {
    if (options.target === "_blank") window.open(myPageUrl, "_blank");
    else window.location.href = myPageUrl;
  }
  return myPageUrl;
}

export default {
  configure,
  purchase,
  purchaseWithSession,
  subscribe,
  handleRedirect,
  getSubscription,
  cancel,
  resume,
  changePlan,
  changeCard,
  openMyPage,
};
